import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Users,
  MessageSquare,
  Clock,
  Award,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

export interface ChampionApplicationData {
  // Step 1: Basic Info
  name: string;
  email: string;
  phone: string;
  address: string;
  yearsInCity: number;

  // Step 2: Community Involvement
  organizations: string;
  leadershipRoles: string;
  volunteerWork: string;

  // Step 3: Reach Potential
  neighborsReachable: number;
  socialMediaFollowing: string;
  communityConnections: string;

  // Step 4: Feedback Plan
  feedbackStrategy: string;
  feedbackFrequency: string;

  // Step 5: Communication Skills
  platformDescription: string;

  // Step 6: Time Commitment
  hoursPerMonth: number;
  availability: string;

  // Step 7: Long-term Interest
  governanceInterest: boolean;
  governanceRoles: string;

  // Step 8: References (optional - currently disabled)
  // reference1Name: '',
  // reference1Contact: '',
  // reference2Name: '',
  // reference2Contact: '',
}

export interface ChampionApplicationFormProps {
  cityName: string;
  stateName?: string;
  contactEmail?: string;
  heroImage?: string;
  enableValidation?: boolean;
  /**
   * Required. Must actually persist the submission. There is deliberately no
   * default: a mock default that returned { success: true } showed residents a
   * fake confirmation on any tenant that forgot to pass a real handler.
   */
  onSubmit: (
    data: ChampionApplicationData,
  ) => Promise<{ success: boolean; submissionId?: string; error?: string }>;
  validateForm?: (
    data: ChampionApplicationData,
    step: number,
  ) => { valid: boolean; message?: string };
}

const TOTAL_STEPS = 7;

/**
 * ChampionApplicationForm - Parameterized champion application form
 *
 * Multi-step form for residents to apply to become Community Champions.
 * 8-step process with progress tracking, validation, and custom submission.
 *
 * @example
 * ```tsx
 * <ChampionApplicationForm
 *   cityName="Houston"
 *   stateName="Texas"
 *   contactEmail="champions@houston.community"
 *   heroImage="/images/houston-champion-hero.png"
 *   enableValidation={true}
 *   onSubmit={handleChampionSubmit}
 * />
 * ```
 */
export function ChampionApplicationForm({
  cityName,
  stateName = '',
  contactEmail = '',
  heroImage = '/images/champion-hero-default.png',
  enableValidation = false,
  onSubmit,
  validateForm,
}: ChampionApplicationFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isInitialMount = useRef(true);

  const cityStateDisplay = stateName ? `${cityName}, ${stateName}` : cityName;

  const [formData, setFormData] = useState<ChampionApplicationData>({
    name: '',
    email: '',
    phone: '',
    address: '',
    yearsInCity: 0,
    organizations: '',
    leadershipRoles: '',
    volunteerWork: '',
    neighborsReachable: 0,
    socialMediaFollowing: '',
    communityConnections: '',
    feedbackStrategy: '',
    feedbackFrequency: '',
    platformDescription: '',
    hoursPerMonth: 0,
    availability: '',
    governanceInterest: false,
    governanceRoles: '',
    reference1Name: '',
    reference1Contact: '',
    reference2Name: '',
    reference2Contact: '',
  });

  // Scroll to top only on initial page load
  useEffect(() => {
    if (isInitialMount.current) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      isInitialMount.current = false;
    }
  }, []);

  const updateField = (field: keyof ChampionApplicationData, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const defaultValidation = (
    data: ChampionApplicationData,
    step: number,
  ): { valid: boolean; message?: string } => {
    if (!enableValidation) return { valid: true };

    switch (step) {
      case 1:
        if (!data.name.trim()) return { valid: false, message: 'Name is required' };
        if (!data.email.trim()) return { valid: false, message: 'Email is required' };
        if (!data.phone.trim()) return { valid: false, message: 'Phone is required' };
        if (!data.address.trim()) return { valid: false, message: 'Address is required' };
        if (data.yearsInCity <= 0)
          return { valid: false, message: `Years in ${cityName} is required` };
        break;

      case 2:
        if (!data.organizations.trim())
          return { valid: false, message: "Please list organizations you're involved with" };
        break;

      case 3:
        if (data.neighborsReachable <= 0)
          return { valid: false, message: 'Please estimate neighbors reachable' };
        if (!data.socialMediaFollowing.trim())
          return { valid: false, message: 'Please describe your social media presence' };
        break;

      case 4:
        if (!data.feedbackStrategy.trim())
          return { valid: false, message: 'Please describe your feedback strategy' };
        if (!data.feedbackFrequency.trim())
          return { valid: false, message: 'Please specify feedback frequency' };
        break;

      case 5:
        if (!data.platformDescription.trim())
          return { valid: false, message: 'Please describe the platform' };
        if (data.platformDescription.split(' ').length < 10) {
          return { valid: false, message: 'Description should be at least 2-3 sentences' };
        }
        break;

      case 6:
        if (data.hoursPerMonth <= 0)
          return { valid: false, message: 'Please specify hours per month' };
        if (!data.availability.trim())
          return { valid: false, message: 'Please describe your availability' };
        break;
    }

    return { valid: true };
  };

  const validateStep = (step: number): { valid: boolean; message?: string } => {
    const validator = validateForm || defaultValidation;
    return validator(formData, step);
  };

  const nextStep = () => {
    const validation = validateStep(currentStep);
    if (!validation.valid) {
      setError(validation.message || 'Please complete all required fields');
      return;
    }

    setError(null);
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    setError(null);
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const validation = validateStep(currentStep);
    if (!validation.valid) {
      setError(validation.message || 'Please complete all required fields');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await onSubmit(formData);

      if (response.success) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError(response.error || 'An error occurred. Please try again.');
      }
    } catch (err) {
      setError('An error occurred while submitting your application. Please try again.');
      console.error('Submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background py-12 sm:py-16">
        <div className="container px-4 sm:px-6 max-w-2xl mx-auto">
          <Card className="text-center">
            <CardHeader className="space-y-4 pt-8">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-3xl">Application Submitted!</CardTitle>
              <CardDescription className="text-lg">
                Thank you for applying to become a Community Champion
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pb-8">
              <div className="bg-primary/5 rounded-lg p-6 text-left space-y-3">
                <h3 className="font-semibold text-lg">What Happens Next?</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex gap-2">
                    <span className="text-primary font-bold">1.</span>
                    <span>
                      Your application will be carefully reviewed by our team within 5-7 business
                      days
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-primary font-bold">2.</span>
                    <span>
                      We evaluate applications based on community involvement, reach potential,
                      feedback strategy, and overall compatibility
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-primary font-bold">3.</span>
                    <span>
                      Top applicants may be contacted for a brief follow-up conversation (individual
                      or small group)
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-primary font-bold">4.</span>
                    <span>
                      Selected Champions will receive an acceptance email with dashboard login
                      credentials and next steps
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-primary font-bold">5.</span>
                    <span>
                      Not selected applicants will be notified and may be considered for future
                      cohorts if we expand
                    </span>
                  </li>
                </ul>
              </div>

              <Alert className="border-amber-300 bg-amber-50">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-sm text-amber-800">
                  <strong>Important Reminder:</strong> Application submission does not guarantee
                  selection. We're looking for Champions who are the right fit for {cityName}, not
                  just completed forms. Additionally, platform launch and lifetime benefits are
                  contingent on sufficient Champion participation and demonstrated community
                  interest.
                </AlertDescription>
              </Alert>

              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  If selected, you'll gain early access to the Champions Dashboard where you can
                  test features, collect feedback from neighbors, and help shape the platform before
                  any public launch decision is made.
                </p>
                <p className="text-sm text-muted-foreground">
                  {/* Questions? Email us at <a href={`mailto:${contactEmail}`} className="text-primary hover:underline font-medium">{contactEmail}</a> */}
                </p>
                <Button onClick={() => (window.location.href = '/')} className="mt-4">
                  Return to Home Page
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const progress = (currentStep / TOTAL_STEPS) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background py-12 sm:py-16">
      <div className="container px-4 sm:px-6 max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 space-y-4">
          <div className="relative h-84 sm:h-64 w-full mb-6 rounded-xl overflow-hidden shadow-lg">
            <img
              src={heroImage}
              alt={`${cityName} Community Champions`}
              className="w-full h-full object-cover"
            />
          </div>
          <Badge variant="outline" className="text-sm px-4 py-1">
            Community Champion Application
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-bold">Become a Community Champion</h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Help shape the future of civic engagement in {cityStateDisplay}. This application takes
            about 10-15 minutes to complete.
          </p>
        </div>

        {/* Important Disclaimer */}
        <Alert className="mb-6 border-amber-300 bg-amber-50">
          <AlertCircle className="h-5 w-5 text-amber-600" />
          <div className="ml-2">
            <h3 className="font-semibold text-amber-900 mb-2">
              Important: Application Does Not Guarantee Selection
            </h3>
            <div className="text-sm text-amber-800 space-y-2">
              <p>
                <strong>
                  Submitting this application does not guarantee you will be selected as a Community
                  Champion.
                </strong>{' '}
                We carefully review all applications and select Champions based on community
                involvement, reach potential, communication skills, and overall fit with the
                program.
              </p>
              <p>
                Our selection process includes reviewing written applications and may include
                follow-up conversations (individually or in small groups) to assess compatibility
                and commitment. We're looking for Champions who are genuinely excited to help build
                something meaningful for {cityName}.
              </p>
              <p className="pt-2 border-t border-amber-200">
                <strong>Platform launch is conditional:</strong> Even if selected, the lifetime Pro
                access benefit and platform launch depend on achieving sufficient Champion
                participation <em>and</em> demonstrated community interest. If we don't reach the
                necessary thresholds for a successful launch, the platform may not go live in{' '}
                {cityName}.
              </p>
            </div>
          </div>
        </Alert>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>
              Step {currentStep} of {TOTAL_STEPS}
            </span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {currentStep === 1 && (
                <>
                  <Users className="w-5 h-5" /> Basic Information
                </>
              )}
              {currentStep === 2 && (
                <>
                  <Award className="w-5 h-5" /> Community Involvement
                </>
              )}
              {currentStep === 3 && (
                <>
                  <Users className="w-5 h-5" /> Reach Potential
                </>
              )}
              {currentStep === 4 && (
                <>
                  <MessageSquare className="w-5 h-5" /> Feedback Plan
                </>
              )}
              {currentStep === 5 && (
                <>
                  <MessageSquare className="w-5 h-5" /> Communication Skills
                </>
              )}
              {currentStep === 6 && (
                <>
                  <Clock className="w-5 h-5" /> Time Commitment
                </>
              )}
              {currentStep === 7 && (
                <>
                  <Award className="w-5 h-5" /> Long-term Interest
                </>
              )}
              {/* References disabled - not supported by API
              {currentStep === 8 && <><Users className="w-5 h-5" /> References (Optional)</>}
              */}
            </CardTitle>
            <CardDescription>
              {currentStep === 1 && 'Tell us about yourself'}
              {currentStep === 2 && '30 points - Your community involvement and leadership'}
              {currentStep === 3 && '20 points - How many neighbors can you reach?'}
              {currentStep === 4 && '20 points - How will you gather feedback?'}
              {currentStep === 5 && '15 points - Explain the platform in your own words'}
              {currentStep === 6 && '10 points - How much time can you dedicate?'}
              {currentStep === 7 && '5 points - Interest in future governance roles'}
              {/* References disabled - not supported by API
              {currentStep === 8 && 'Optional - 1-2 neighbors who can vouch for you'}
              */}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Step 1: Basic Info */}
            {currentStep === 1 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="John Smith"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="john@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="(555) 555-0123"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address in {cityName} *</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    placeholder={`123 Main St, ${cityStateDisplay}`}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="years">How many years have you lived in {cityName}? *</Label>
                  <Input
                    id="years"
                    type="number"
                    min="0"
                    value={formData.yearsInCity || ''}
                    onChange={(e) => updateField('yearsInCity', parseInt(e.target.value) || 0)}
                    placeholder="5"
                  />
                </div>
              </>
            )}

            {/* Step 2: Community Involvement */}
            {currentStep === 2 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="organizations">
                    What community organizations or groups are you involved with? *
                  </Label>
                  <Textarea
                    id="organizations"
                    value={formData.organizations}
                    onChange={(e) => updateField('organizations', e.target.value)}
                    placeholder="Example: Neighborhood association, church groups, volunteer organizations, etc."
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground">
                    List any neighborhood associations, church groups, volunteer organizations, or
                    community groups
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="leadership">
                    What leadership roles have you held in the community?
                  </Label>
                  <Textarea
                    id="leadership"
                    value={formData.leadershipRoles}
                    onChange={(e) => updateField('leadershipRoles', e.target.value)}
                    placeholder="Example: HOA board member, church deacon, volunteer coordinator, etc."
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground">
                    Include formal positions (HOA board, etc.) and informal leadership (organizing
                    events, etc.)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="volunteer">
                    Describe your volunteer work or community service
                  </Label>
                  <Textarea
                    id="volunteer"
                    value={formData.volunteerWork}
                    onChange={(e) => updateField('volunteerWork', e.target.value)}
                    placeholder="Example: Organize neighborhood cleanups, volunteer at food bank, etc."
                    rows={4}
                  />
                </div>
              </>
            )}

            {/* Step 3: Reach Potential */}
            {currentStep === 3 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="reachable">
                    Approximately how many {cityName} residents can you reach directly? *
                  </Label>
                  <Input
                    id="reachable"
                    type="number"
                    min="0"
                    value={formData.neighborsReachable || ''}
                    onChange={(e) =>
                      updateField('neighborsReachable', parseInt(e.target.value) || 0)
                    }
                    placeholder="50"
                  />
                  <p className="text-xs text-muted-foreground">
                    Include neighbors, friends, family, co-workers, and community contacts
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="social">
                    Describe your social media presence in {cityName} *
                  </Label>
                  <Textarea
                    id="social"
                    value={formData.socialMediaFollowing}
                    onChange={(e) => updateField('socialMediaFollowing', e.target.value)}
                    placeholder={`Example: Active in '${cityName} Community' Facebook group, Nextdoor neighborhood lead, etc.`}
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground">
                    List Facebook groups, Nextdoor presence, or other local social media you're
                    active in
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="connections">
                    How are you connected to the {cityName} community?
                  </Label>
                  <Textarea
                    id="connections"
                    value={formData.communityConnections}
                    onChange={(e) => updateField('communityConnections', e.target.value)}
                    placeholder="Example: Know most neighbors on my street, attend local events, active in church, etc."
                    rows={4}
                  />
                </div>
              </>
            )}

            {/* Step 4: Feedback Plan */}
            {currentStep === 4 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="strategy">
                    How will you gather feedback from your neighbors about the platform? *
                  </Label>
                  <Textarea
                    id="strategy"
                    value={formData.feedbackStrategy}
                    onChange={(e) => updateField('feedbackStrategy', e.target.value)}
                    placeholder="Example: Post in Facebook groups, talk to neighbors in person, send survey links, host small gatherings, etc."
                    rows={5}
                  />
                  <p className="text-xs text-muted-foreground">
                    Be specific - how will you collect honest feedback from at least 5-10 neighbors?
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="frequency">How often will you collect feedback? *</Label>
                  <Textarea
                    id="frequency"
                    value={formData.feedbackFrequency}
                    onChange={(e) => updateField('feedbackFrequency', e.target.value)}
                    placeholder="Example: Weekly check-ins with 3-5 neighbors, monthly surveys to broader group"
                    rows={3}
                  />
                </div>
              </>
            )}

            {/* Step 5: Communication Skills */}
            {currentStep === 5 && (
              <div className="space-y-2">
                <Label htmlFor="description">
                  In your own words, describe what this platform does in 2-3 sentences *
                </Label>
                <Textarea
                  id="description"
                  value={formData.platformDescription}
                  onChange={(e) => updateField('platformDescription', e.target.value)}
                  placeholder={`Example: This platform helps ${cityName} residents vote on community issues, organize group purchases to save money, and communicate with neighbors securely...`}
                  rows={5}
                />
                <p className="text-xs text-muted-foreground">
                  Imagine explaining this to a neighbor - keep it simple and clear
                </p>
              </div>
            )}

            {/* Step 6: Time Commitment */}
            {currentStep === 6 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="hours">
                    How many hours per month can you dedicate to being a Champion? *
                  </Label>
                  <Input
                    id="hours"
                    type="number"
                    min="0"
                    value={formData.hoursPerMonth || ''}
                    onChange={(e) => updateField('hoursPerMonth', parseInt(e.target.value) || 0)}
                    placeholder="10"
                  />
                  <p className="text-xs text-muted-foreground">
                    We estimate Champions spend 5-15 hours per month during the 2-month testing
                    period
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="availability">When are you typically available? *</Label>
                  <Textarea
                    id="availability"
                    value={formData.availability}
                    onChange={(e) => updateField('availability', e.target.value)}
                    placeholder="Example: Evenings after 6pm, weekends, flexible schedule during the day"
                    rows={3}
                  />
                </div>
              </>
            )}

            {/* Step 7: Long-term Interest */}
            {currentStep === 7 && (
              <>
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="governance"
                    checked={formData.governanceInterest}
                    onCheckedChange={(checked) =>
                      updateField('governanceInterest', checked as boolean)
                    }
                  />
                  <div className="space-y-1">
                    <Label htmlFor="governance" className="cursor-pointer">
                      I'm interested in future governance roles after launch
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Examples: Community moderator, dispute resolution, feature voting oversight
                    </p>
                  </div>
                </div>

                {formData.governanceInterest && (
                  <div className="space-y-2">
                    <Label htmlFor="roles">Which governance roles interest you?</Label>
                    <Textarea
                      id="roles"
                      value={formData.governanceRoles}
                      onChange={(e) => updateField('governanceRoles', e.target.value)}
                      placeholder="Example: I'd like to help moderate discussions and resolve disputes between neighbors"
                      rows={4}
                    />
                  </div>
                )}
              </>
            )}

            {/* Step 8: References - DISABLED
            References are not supported by the current API.
            If this feature is needed in the future, uncomment the code below and update TOTAL_STEPS to 8.
            
            {currentStep === 8 && (
              <>
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    References are optional but help us verify your community involvement.
                    Please provide 1-2 {cityName} residents who can vouch for you.
                  </AlertDescription>
                </Alert>

                <div className="space-y-4 border-t pt-4">
                  <h3 className="font-semibold">Reference 1</h3>
                  <div className="space-y-2">
                    <Label htmlFor="ref1name">Name</Label>
                    <Input
                      id="ref1name"
                      value={formData.reference1Name}
                      onChange={(e) => updateField('reference1Name', e.target.value)}
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ref1contact">Email or Phone</Label>
                    <Input
                      id="ref1contact"
                      value={formData.reference1Contact}
                      onChange={(e) => updateField('reference1Contact', e.target.value)}
                      placeholder="jane@example.com or (555) 555-0456"
                    />
                  </div>
                </div>

                <div className="space-y-4 border-t pt-4">
                  <h3 className="font-semibold">Reference 2</h3>
                  <div className="space-y-2">
                    <Label htmlFor="ref2name">Name</Label>
                    <Input
                      id="ref2name"
                      value={formData.reference2Name}
                      onChange={(e) => updateField('reference2Name', e.target.value)}
                      placeholder="Bob Smith"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ref2contact">Email or Phone</Label>
                    <Input
                      id="ref2contact"
                      value={formData.reference2Contact}
                      onChange={(e) => updateField('reference2Contact', e.target.value)}
                      placeholder="bob@example.com or (555) 555-0789"
                    />
                  </div>
                </div>
              </>
            )}
            */}
          </CardContent>

          {/* Navigation Buttons */}
          <div className="border-t px-6 py-4 flex justify-between items-center bg-muted/30">
            <Button variant="outline" onClick={prevStep} disabled={currentStep === 1}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            {currentStep < TOTAL_STEPS ? (
              <Button onClick={nextStep}>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
